import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, darken, useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useIntl } from 'react-intl';

const reasons = {
  InvalidCredentials: 'credentials',
  ManualSynchronizationRequired: 'credentials',
  MissingAccountConfiguration: 'configuration',
  AccountIdentityMismatch: 'identity',
  IdentityValidationInconclusive: 'inconclusive',
  ProviderUnavailable: 'provider',
  NetworkFailure: 'connection',
  HttpTimeout: 'connection',
  AccountTimeout: 'connection',
  AccountLeaseLost: 'concurrency',
  InvalidResponse: 'response',
  ProviderRejected: 'response',
};

// Consome somente as propriedades lowercase devolvidas pelo backend.
export default function InstagramTokenRenewalAlert({ registration, compact = false }) {
  const intl = useIntl();
  const theme = useTheme();
  const status = Number(registration?.tokenrenewalstatus);
  if (Number(registration?.messageproviderid) !== 3 || (status !== 2 && status !== 3)) return null;

  const manual = status === 3;
  const label = intl.formatMessage({ id: `instagram.renewal.${manual ? 'action.required' : 'temporary.failure'}` });
  const reason = intl.formatMessage({ id: `instagram.renewal.reason.${reasons[registration.tokenrenewalerrorcode] || 'general'}` });
  const instruction = intl.formatMessage({ id: `instagram.renewal.${manual ? 'manual.instruction' : 'retry.instruction'}` });
  const failedAt = registration.tokenrenewalfailedat ? new Date(registration.tokenrenewalfailedat) : null;
  const date = failedAt && !Number.isNaN(failedAt.getTime())
    ? intl.formatMessage({ id: 'instagram.renewal.failed.since' }, {
      date: `${intl.formatDate(failedAt)} ${intl.formatTime(failedAt, { hour: '2-digit', minute: '2-digit' })}`,
    }) : '';
  const Icon = manual ? ErrorOutlineIcon : WarningAmberIcon;
  const palette = theme.palette[manual ? 'error' : 'warning'];
  const alertColor = theme.palette.mode === 'dark' ? palette.light : darken(palette.dark, 0.35);

  return (
    <Tooltip title={<Box>{reason}<br />{instruction}{date && <><br />{date}</>}</Box>} arrow>
      <Box
        component="span"
        tabIndex={0}
        role="note"
        aria-label={`${label}. ${reason} ${instruction} ${date}`}
        sx={{
          display: 'flex', alignItems: compact ? 'center' : 'flex-start', gap: 0.75,
          mt: compact ? 0 : 1, mb: compact ? 0 : 2, maxWidth: '100%',
          ...(compact && {
            px: 1, py: 0.5, minHeight: 28, boxSizing: 'border-box', width: 'fit-content',
            border: '1px solid', borderColor: alpha(palette.main, 0.4), borderRadius: 1,
            bgcolor: alpha(palette.main, 0.12), color: alertColor,
          }),
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 3 },
        }}
      >
        {/* A cor explícita preserva a gravidade diante do estilo global que colore os ícones de verde. */}
        <Icon style={{ color: alertColor }} sx={{ fontSize: compact ? 18 : 20, flexShrink: 0 }} />
        <Box component="span" sx={{ minWidth: 0 }}>
          <Typography component="span" color="inherit" variant="body2" sx={{
            fontWeight: compact ? 700 : 600, display: 'block',
            ...(compact && { fontSize: '13px', lineHeight: '18px', letterSpacing: 0 }),
          }}>
            {label}
          </Typography>
          {!compact && <Typography component="span" variant="body2">{reason} {instruction}</Typography>}
        </Box>
      </Box>
    </Tooltip>
  );
}
